/**
 * ProductFilters Component
 *
 * Accordion-based faceted filter sidebar matching Shapermint's design:
 * - Flat layout (no card shadow)
 * - Collapsible sections with chevron toggle
 * - Size rendered as button grid
 * - Checkbox-based filters for other facets
 */

import React, { useState, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { CollectionFacets, SearchFilters } from '../../../domain/entities/collection.entity';

/* ---------- container ---------------------------------------------------- */

const FiltersContainer = styled.aside`
  width: 100%;
`;

/* ---------- accordion section -------------------------------------------- */

const Section = styled.div`
  border-bottom: 1px solid #e5e7eb;
  padding: 0;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #292929;
  text-align: left;

  &:hover {
    color: #000;
  }
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #292929;
`;

const ChevronIcon = styled.svg<{ $open: boolean }>`
  width: 12px;
  height: 12px;
  color: #666;
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const SectionContent = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  padding-bottom: 14px;
`;

/* ---------- checkbox filters --------------------------------------------- */

const FilterRow = styled.label<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  cursor: pointer;
  font-size: 14px;
  color: ${({ $active }) => ($active ? '#292929' : '#4b5563')};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};

  &:hover {
    color: #292929;
  }
`;

const CheckboxContainer = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #292929;
`;

const Count = styled.span`
  color: #9ca3af;
  font-size: 12px;
`;

/* ---------- size button grid --------------------------------------------- */

const SizeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SizeButton = styled.button<{ $active: boolean }>`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${({ $active }) => ($active ? '#292929' : '#e0e0e0')};
  border-radius: 4px;
  background: ${({ $active }) => ($active ? '#292929' : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#292929')};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #292929;
  }
`;

/* ---------- active filter chips ------------------------------------------ */

const ActiveFiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 0;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  background: #faf5f0;
  border: 1px solid #e8ddd0;
  border-radius: 4px;
  color: #292929;
  cursor: pointer;

  &:hover {
    background: #f0e8dd;
  }
`;

const ClearAllButton = styled.button`
  font-size: 12px;
  color: #666;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;

  &:hover {
    color: #292929;
  }
`;

/* ---------- empty facet text --------------------------------------------- */

const EmptyFacetText = styled.span`
  font-size: 13px;
  color: #9ca3af;
  padding: 4px 0;
  display: block;
`;

/* ---------- price range -------------------------------------------------- */

const PRICE_RANGES = [
  { label: 'Under $25', min: undefined, max: 25 },
  { label: '$25-$50', min: 25, max: 50 },
  { label: '$50-$75', min: 50, max: 75 },
  { label: '$75 And Above', min: 75, max: undefined },
] as const;

const SIZE_VALUES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

/* ---------- chevron svg helper ------------------------------------------- */

function Chevron({ open }: Readonly<{ open: boolean }>) {
  return (
    <ChevronIcon $open={open} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </ChevronIcon>
  );
}

/* ---------- main component ----------------------------------------------- */

interface ProductFiltersProps {
  facets: CollectionFacets;
  filters: SearchFilters;
  onFilterChange: (filters: SearchFilters) => void;
}

export function ProductFilters({ facets, filters, onFilterChange }: Readonly<ProductFiltersProps>) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    size: true,
    price: true,
  });

  const toggle = useCallback((key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleOptionToggle = useCallback(
    (optionName: string, optionValue: string) => {
      const currentOptions = filters.options || {};
      const newOptions = { ...currentOptions };

      if (newOptions[optionName] === optionValue) {
        delete newOptions[optionName];
      } else {
        newOptions[optionName] = optionValue;
      }

      onFilterChange({ ...filters, options: newOptions, page: 1 });
    },
    [filters, onFilterChange],
  );

  const isPriceRangeActive = useCallback(
    (min: number | undefined, max: number | undefined) =>
      filters.priceMin === min && filters.priceMax === max,
    [filters.priceMin, filters.priceMax],
  );

  const handlePriceRangeToggle = useCallback(
    (min: number | undefined, max: number | undefined) => {
      if (isPriceRangeActive(min, max)) {
        onFilterChange({ ...filters, priceMin: undefined, priceMax: undefined, page: 1 });
      } else {
        onFilterChange({ ...filters, priceMin: min, priceMax: max, page: 1 });
      }
    },
    [filters, onFilterChange, isPriceRangeActive],
  );

  const handleClearAll = useCallback(() => {
    onFilterChange({ q: filters.q, page: 1, limit: filters.limit });
  }, [filters.q, filters.limit, onFilterChange]);

  const removeFilter = useCallback(
    (type: string, value?: string) => {
      if (type === 'price') {
        onFilterChange({ ...filters, priceMin: undefined, priceMax: undefined, page: 1 });
      } else if (type.startsWith('option:') && value) {
        const optionName = type.replace('option:', '');
        handleOptionToggle(optionName, value);
      }
    },
    [filters, onFilterChange, handleOptionToggle],
  );

  const hasActiveFilters =
    filters.priceMin !== undefined ||
    filters.priceMax !== undefined ||
    (filters.options && Object.keys(filters.options).length > 0);

  // Build stable facets: remember every facet key+values we've ever seen so
  // that selecting a filter value (e.g. Color=red) doesn't cause the section
  // to disappear when the backend returns a narrower facet set.
  const stableFacetsRef = useRef<CollectionFacets>({});

  const dynamicKeys = Object.keys(facets).filter(
    (key) => !['tags', 'status', 'price_ranges'].includes(key),
  );

  // Merge: keep all previously-seen keys; update values from the latest response
  for (const key of dynamicKeys) {
    stableFacetsRef.current[key] = facets[key];
  }

  const stableDynamicFacets = Object.entries(stableFacetsRef.current).filter(
    ([key]) => key.toLowerCase() !== 'size',
  );

  return (
    <FiltersContainer>
      {/* Active filters */}
      {hasActiveFilters && (
        <div style={{ paddingBottom: 8 }}>
          <ActiveFiltersRow>
            {(filters.priceMin !== undefined || filters.priceMax !== undefined) && (
              <FilterChip onClick={() => removeFilter('price')}>
                {PRICE_RANGES.find((r) => isPriceRangeActive(r.min, r.max))?.label
                  ?? `$${filters.priceMin ?? 0}–$${filters.priceMax ?? '∞'}`}{' '}
                &times;
              </FilterChip>
            )}
            {Object.entries(filters.options || {}).map(([key, value]) => (
              <FilterChip key={key} onClick={() => removeFilter(`option:${key}`, value)}>
                {key}: {value} &times;
              </FilterChip>
            ))}
          </ActiveFiltersRow>
          <ClearAllButton onClick={handleClearAll}>Clear all</ClearAllButton>
        </div>
      )}

      {/* Size section (always visible) */}
      <Section>
        <SectionHeader onClick={() => toggle('size')}>
          <SectionTitle>Size</SectionTitle>
          <Chevron open={openSections.size ?? true} />
        </SectionHeader>
        <SectionContent $open={openSections.size ?? true}>
          <SizeGrid>
            {SIZE_VALUES.map((size) => {
              const isActive = filters.options?.['Size'] === size;
              return (
                <SizeButton
                  key={size}
                  $active={isActive}
                  onClick={() => handleOptionToggle('Size', size)}
                >
                  {size}
                </SizeButton>
              );
            })}
          </SizeGrid>
        </SectionContent>
      </Section>

      {/* Dynamic facet sections (stable -- sections never disappear once seen) */}
      {stableDynamicFacets.map(([optionName, values]) => {
        const isOpen = openSections[optionName] ?? true;
        return (
          <Section key={optionName}>
            <SectionHeader onClick={() => toggle(optionName)}>
              <SectionTitle>{optionName}</SectionTitle>
              <Chevron open={isOpen} />
            </SectionHeader>
            <SectionContent $open={isOpen}>
              {values.length > 0 ? (
                values.slice(0, 12).map((facet) => {
                  const isActive = filters.options?.[optionName] === facet.value;
                  return (
                    <FilterRow key={facet.value} $active={isActive}>
                      <CheckboxContainer>
                        <Checkbox
                          type="checkbox"
                          checked={isActive}
                          onChange={() => handleOptionToggle(optionName, facet.value)}
                        />
                        {facet.value}
                      </CheckboxContainer>
                      <Count>({facet.count})</Count>
                    </FilterRow>
                  );
                })
              ) : (
                <EmptyFacetText>No options available</EmptyFacetText>
              )}
            </SectionContent>
          </Section>
        );
      })}

      {/* Price Range section (always visible) */}
      <Section>
        <SectionHeader onClick={() => toggle('price')}>
          <SectionTitle>Price Range</SectionTitle>
          <Chevron open={openSections.price ?? true} />
        </SectionHeader>
        <SectionContent $open={openSections.price ?? true}>
          {PRICE_RANGES.map((range) => {
            const active = isPriceRangeActive(range.min, range.max);
            return (
              <FilterRow key={range.label} $active={active}>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    checked={active}
                    onChange={() => handlePriceRangeToggle(range.min, range.max)}
                  />
                  {range.label}
                </CheckboxContainer>
              </FilterRow>
            );
          })}
        </SectionContent>
      </Section>
    </FiltersContainer>
  );
}

export default ProductFilters;
